'use client';


import Header from '@/components/common/Header';
import QuoteInteractive from './components/QuoteInteractive';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

export default function InstantQuotePage() {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-24 lg:pt-32 pb-12 bg-gradient-to-br from-primary to-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-elevated">
                <Icon name="CalculatorIcon" size={32} variant="solid" className="text-primary" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-headline font-bold text-white mb-4">
              {t({ en: 'Get Your Instant Moving Quote', ar: 'احصل على عرض النقل الفوري' })}
            </h1>
            <p className="text-lg lg:text-xl text-white text-opacity-90 font-body mb-6">
              {t({ en: 'Transparent pricing in 3 simple steps. No hidden fees, no surprises.', ar: 'أسعار شفافة في 3 خطوات بسيطة. لا رسوم خفية، لا مفاجآت.' })}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={20} variant="solid" />
                <span className="font-body">{t({ en: 'Takes 2 minutes', ar: 'يستغرق دقيقتين' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={20} variant="solid" />
                <span className="font-body">{t({ en: 'No commitment required', ar: 'لا يتطلب التزام' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CurrencyDollarIcon" size={20} variant="solid" />
                <span className="font-body">{t({ en: 'Instant pricing', ar: 'تسعير فوري' })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12">
        <QuoteInteractive />
      </section>
      {/* Trust Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-headline font-bold text-text-primary text-center mb-12">
              {t({ en: 'Why Choose Almanara Moving?', ar: 'لماذا تختار المنارة للنقل؟' })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-success bg-opacity-10 rounded-full flex items-center justify-center">
                    <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-success" />
                  </div>
                </div>
                <h3 className="text-xl font-headline font-bold text-text-primary mb-2">
                  {t({ en: 'Fully Insured', ar: 'مؤمن بالكامل' })}
                </h3>
                <p className="text-text-secondary font-body">
                  {t({ en: 'Comprehensive insurance coverage for complete peace of mind during your move', ar: 'تغطية تأمينية شاملة لراحة البال الكاملة أثناء نقلك' })}
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                    <Icon name="UserGroupIcon" size={32} variant="solid" className="text-primary" />
                  </div>
                </div>
                <h3 className="text-xl font-headline font-bold text-text-primary mb-2">
                  {t({ en: 'Expert Team', ar: 'فريق خبير' })}
                </h3>
                <p className="text-text-secondary font-body">
                  {t({ en: 'Professional movers with years of experience handling all types of relocations', ar: 'عمال نقل محترفون بسنوات من الخبرة في التعامل مع جميع أنواع عمليات النقل' })}
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-accent bg-opacity-10 rounded-full flex items-center justify-center">
                    <Icon name="StarIcon" size={32} variant="solid" className="text-accent" />
                  </div>
                </div>
                <h3 className="text-xl font-headline font-bold text-text-primary mb-2">
                  {t({ en: '5-Star Service', ar: 'خدمة 5 نجوم' })}
                </h3>
                <p className="text-text-secondary font-body">
                  {t({ en: 'Trusted by 500+ satisfied customers across Kuwait with excellent reviews', ar: 'موثوق به من قبل أكثر من 500 عميل راضٍ في جميع أنحاء الكويت مع تقييمات ممتازة' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-white mb-4">
              {t({ en: 'Need Help with Your Quote?', ar: 'هل تحتاج مساعدة في عرض الأسعار؟' })}
            </h2>
            <p className="text-lg text-white text-opacity-90 font-body mb-8">
              {t({ en: 'Our moving experts are available 24/7 to answer your questions and help you plan your perfect move', ar: 'خبراء النقل لدينا متاحون على مدار الساعة طوال أيام الأسبوع للإجابة على أسئلتك ومساعدتك في التخطيط لنقلك المثالي' })}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:69911181"
                className="flex items-center justify-center space-x-2 px-8 py-4 rounded-lg bg-white text-primary hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
              >
                <Icon name="PhoneIcon" size={24} variant="solid" />
                <span>{t({ en: 'Call 6991 1181', ar: 'اتصل 6991 1181' })}</span>
              </a>
              <a
                href="https://wa.me/96569911181"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 px-8 py-4 rounded-lg bg-success text-white hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
                <span>{t({ en: 'WhatsApp Us', ar: 'واتساب' })}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="relative w-10 h-10">
                <svg
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-full h-full"
                >
                  <circle cx="24" cy="24" r="22" fill="var(--color-primary)" />
                  <path
                    d="M24 8L24 28M24 28L16 20M24 28L32 20"
                    stroke="var(--color-accent)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="24" cy="32" r="3" fill="var(--color-accent)" />
                  <path
                    d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38"
                    stroke="var(--color-secondary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-headline font-bold text-primary leading-tight">
                  Almanara
                </span>
                <span className="text-xs font-body text-text-secondary leading-tight">
                  Moving
                </span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p className="text-text-secondary font-body text-sm">
                &copy; {new Date()?.getFullYear()} Almanara Moving. All rights reserved.
              </p>
              <p className="text-text-secondary font-body text-sm">
                Made by <a href="https://codet.kw" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-accent transition-colors">codet.kw</a>
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="https://instagram.com/almanara_3985"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors duration-base"
                aria-label="Follow us on Instagram"
              >
                <Icon name="CameraIcon" size={20} variant="outline" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}